import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { MessageSquare, Send, Clock, User, Mail, CheckCircle, AlertCircle, ChevronDown, ChevronUp, FileText, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatDate, formatTimestamp } from '@/utils/dateFormat';

/**
 * BOQ Comments Management Page
 * 
 * Features:
 * - View all BOQ comments from clients/vendors
 * - Reply to comments (sends email to original commenter)
 * - Track comment status (pending, responded, resolved)
 * - Threaded conversation view
 */
export default function BOQComments() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedComment, setSelectedComment] = useState(null);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedComments, setExpandedComments] = useState({});

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchComments();
  }, [statusFilter]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/boq-comments${params}`);
      setComments(response.data);
    } catch (error) {
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async () => {
    if (!selectedComment || !replyMessage.trim()) return;
    
    setSendingReply(true);
    try {
      await axios.post(`${API}/boq-comments/${selectedComment.id}/reply`, {
        message: replyMessage.trim()
      });
      toast.success('Reply sent successfully');
      setReplyDialogOpen(false);
      setReplyMessage('');
      setSelectedComment(null);
      fetchComments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleUpdateStatus = async (commentId, status) => {
    try {
      await axios.patch(`${API}/boq-comments/${commentId}/status?new_status=${status}`);
      toast.success(`Status updated to ${status}`);
      fetchComments();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const toggleExpand = (commentId) => {
    setExpandedComments(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pending</Badge>;
      case 'responded':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Responded</Badge>;
      case 'resolved':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Resolved</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (!['owner', 'staff', 'admin'].includes(user.role)) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-stone-500">You don't have permission to view this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
              <MessageSquare className="text-blue-600" />
              BOQ Comments
            </h1>
            <p className="text-stone-500 text-sm">Manage comments from clients and vendors on public BOQ links</p>
          </div>
          
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-stone-500">Filter:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-stone-200 rounded-lg text-sm"
            >
              <option value="all">All Comments</option>
              <option value="pending">Pending</option>
              <option value="responded">Responded</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
        </div>

        {/* Comments List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-12 bg-stone-50 rounded-xl">
            <MessageSquare className="mx-auto text-stone-300 mb-4" size={48} />
            <p className="text-stone-500">No comments found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => {
              const isExpanded = expandedComments[comment.id];
              const hasReplies = comment.replies && comment.replies.length > 0;
              
              return (
                <div 
                  key={comment.id} 
                  className="bg-white border border-stone-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                >
                  {/* Comment Header */}
                  <div className="p-4 border-b border-stone-100">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          comment.view_type === 'vendor' ? 'bg-purple-100' : 'bg-blue-100'
                        }`}>
                          <User className={comment.view_type === 'vendor' ? 'text-purple-600' : 'text-blue-600'} size={20} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-stone-900">{comment.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {comment.view_type === 'vendor' ? 'Vendor' : 'Client'}
                            </Badge>
                            {getStatusBadge(comment.status)}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-stone-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Mail size={12} /> {comment.email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={12} /> {formatTimestamp(comment.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedComment(comment);
                            setReplyDialogOpen(true);
                          }}
                          className="text-blue-600"
                        >
                          <Send size={14} className="mr-1" />
                          Reply
                        </Button>
                      </div>
                    </div>
                    
                    {/* BOQ Reference */}
                    <div className="flex items-center gap-4 mt-3 text-xs text-stone-500">
                      <span className="flex items-center gap-1">
                        <FileText size={12} /> {comment.worksheet_number}
                      </span>
                      <span className="flex items-center gap-1">
                        <Building2 size={12} /> {comment.project_name}
                      </span>
                    </div>
                  </div>
                  
                  {/* Comment Body */}
                  <div className="p-4 bg-stone-50">
                    <p className="text-stone-700 whitespace-pre-wrap">{comment.comment}</p>
                  </div>
                  
                  {/* Replies Thread */}
                  {hasReplies && (
                    <div className="border-t border-stone-100">
                      <button
                        onClick={() => toggleExpand(comment.id)}
                        className="w-full px-4 py-2 flex items-center justify-between text-sm text-stone-600 hover:bg-stone-50"
                      >
                        <span>
                          {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
                        </span>
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      
                      {isExpanded && (
                        <div className="px-4 pb-4 space-y-3">
                          {comment.replies.map((reply, idx) => (
                            <div 
                              key={reply.id || idx} 
                              className={`p-3 rounded-lg border ${
                                reply.sender_role === 'admin' 
                                  ? 'bg-blue-50 border-blue-200 ml-8' 
                                  : 'bg-stone-100 border-stone-200'
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-medium text-sm">
                                  {reply.sender_name || (reply.sender_role === 'admin' ? 'Admin' : 'User')}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  {reply.sender_role}
                                </Badge>
                                <span className="text-xs text-stone-500">
                                  {formatTimestamp(reply.timestamp)}
                                </span>
                              </div>
                              <p className="text-sm text-stone-700 whitespace-pre-wrap">{reply.message}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Status Actions */}
                  <div className="px-4 py-3 bg-white border-t border-stone-100 flex items-center justify-end gap-2">
                    <span className="text-xs text-stone-500 mr-2">Mark as:</span>
                    {comment.status !== 'pending' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-amber-600 text-xs"
                        onClick={() => handleUpdateStatus(comment.id, 'pending')}
                      >
                        Pending
                      </Button>
                    )}
                    {comment.status !== 'responded' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-blue-600 text-xs"
                        onClick={() => handleUpdateStatus(comment.id, 'responded')}
                      >
                        Responded
                      </Button>
                    )}
                    {comment.status !== 'resolved' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-green-600 text-xs"
                        onClick={() => handleUpdateStatus(comment.id, 'resolved')}
                      >
                        <CheckCircle size={12} className="mr-1" />
                        Resolved
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Reply Dialog */}
        <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="text-blue-600" size={20} />
                Reply to Comment
              </DialogTitle>
            </DialogHeader>
            
            {selectedComment && (
              <div className="space-y-4 py-4">
                {/* Original Comment Preview */}
                <div className="p-3 bg-stone-50 rounded-lg border">
                  <p className="text-xs text-stone-500 mb-1">Replying to {selectedComment.name}:</p>
                  <p className="text-sm text-stone-700 line-clamp-3">{selectedComment.comment}</p>
                </div>
                
                {/* Reply Input */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Your Reply
                  </label>
                  <Textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Type your response..."
                    rows={5}
                    className="w-full"
                  />
                </div>
                
                {/* Info */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  <p>This reply will be:</p>
                  <ul className="text-xs mt-1 space-y-1">
                    <li>• Sent via email to {selectedComment.email}</li>
                    <li>• Saved in the comment thread</li>
                    <li>• Status updated to "Responded"</li>
                  </ul>
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setReplyDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleReply} 
                disabled={sendingReply || !replyMessage.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {sendingReply ? (
                  <>Sending...</>
                ) : (
                  <>
                    <Send size={16} className="mr-2" />
                    Send Reply
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
